import { addYears } from 'date-fns';
import { maxBy } from 'lodash';
import { getTypographyVariants } from '@theme/styleOverrides';
import { TFormField, FormFieldType } from '@models/forms/formFields.type';
import {
  BrokerageProducerSearchResponse,
  PolicyGetResponse,
  PolicyTermSearchResponse,
  PolicyTransactionType,
} from '@lio/api-types';
import {
  EndorsementPolicyDialogType,
  PolicyCancelationReason,
  PolicyDialogType,
  PolicyTransaction,
  PolicyTransactionStatus,
  PolicyTransactionTypes,
  RenewDialogType,
  endorsementReasons,
} from '@models/policyTransaction';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { Item } from '@components/select';
import {
  cancelationMethods,
  cancelationReasons,
  cancelationSources,
  expiredPolicyCancelationMethods,
  expiredPolicyCancelationReasons,
  reinstatementReasons,
} from '../PolicyTransactionDialog.constants';
import { BOOL_ITEMS } from '@pages/PortalAdmin/FormsV2Admin/FormCatalog/EditForm/StepFields/FieldEditor/fieldConfigs';
import { PolicyStatus } from '@lio/rule-engine';
import { Theme } from '@mui/material';
import { InfiniteData } from '@tanstack/react-query';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import {
  getConditionalRenewalGrounds,
  getNonrenewalGrounds,
  isLegalNoticeDaysRequirementMet,
} from './nonrenewalGrounds';

type TPolicyTransactionDialogData = {
  fields: { [fieldName: string]: TFormField };
  title: string;
  transactionType: PolicyTransactionType | 'Rollback';
  buttonConfig?: {
    createButtonText?: string;
    cancelButtonText?: string;
    continueButtonText?: string;
    viewButtonText?: string;
    hidePrimaryButton?: boolean;
  };
  toastConfig?: {
    errorMessage?: string;
    successMessage?: string;
  };
};

export interface ProducerFieldProps {
  loadMore: () => void;
  onDebouncedChange: (value: string) => void;
  brokerageProducers?: InfiniteData<BrokerageProducerSearchResponse> | undefined;
}

export const getPolicyTransactionDialogData = ({
  policy,
  policyTerms,
  dialogType,
  policyTransaction,
  other,
}: {
  policy: PolicyGetResponse;
  policyTerms: PolicyTermSearchResponse['items'];
  dialogType: PolicyDialogType | EndorsementPolicyDialogType | RenewDialogType;
  policyTransaction?: PolicyTransaction;
  other?: {
    billingPlanOptions?: Item[];
    lastProcessedEffectiveDate?: Date;
    lastCancelationEffectiveDate?: Date;
    producerField?: ProducerFieldProps;
    cancelationGroundsOptions?: Item[];
    selectedConditionalGrounds?: {
      grounds1?: string;
      grounds2?: string;
      grounds3?: string;
    };
  };
}): TPolicyTransactionDialogData => {
  const formattedPolicyNumber = getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber);

  const minEffectiveOnDate = policy.inceptionOn;
  let maxExpiresOnDate = policy.expiresOn!;

  if (policyTerms?.length > 0) {
    const expiresOnDates = policyTerms.map((term) => new Date(term.expiresOn));
    maxExpiresOnDate = new Date(Math.max(...expiresOnDates.map((date) => date.getTime())));
  }

  switch (dialogType) {
    case PolicyDialogType.Renew: {
      const expiresOn = policy.expiresOn!;

      const expiresOnDate = new Date(expiresOn);

      // NOTE: code for the original requirements for this date, we may go back to it when OS figures it out (OS only lets us bind with the expires on date right now)
      // const effectiveOn = policy.effectiveOn;
      // const effectiveOnDate = new Date(effectiveOn);
      // const dayDifference = Math.abs(differenceInDays(expiresOnDate, effectiveOnDate));
      // const defaultEffectiveDate = dayDifference >= 120 ? expiresOnDate : addYears(effectiveOnDate, 1);
      const fields: { [fieldName: string]: TFormField } = {
        effectiveDate: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'effectiveDate',
          label: 'Effective On',
          fieldType: FormFieldType.DatePickerField,
          required: true,
          disabled: true,
          defaultValue: fakeLocalMidnightFromTZMidnight(policyTransaction?.effectiveOn ?? expiresOnDate),
          fieldConfig: {
            dateField: policyTransaction?.effectiveOn
              ? {}
              : {
                  minDate: expiresOnDate,
                  maxDate: addYears(new Date(expiresOnDate), 1),
                },
          },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.Renewal,
        fields,
        title: `Renew Policy ${formattedPolicyNumber}`,
        toastConfig: {
          successMessage: 'Policy renewal created',
        },
      };
    }
    case EndorsementPolicyDialogType.Endorsement: {
      const effectiveOnDate = policyTransaction ? policyTransaction?.effectiveOn : (policy?.effectiveOn ?? null);
      const fields: { [fieldName: string]: TFormField } = {
        effectiveOn: {
          gridWidth: { xs: 12, sm: 6, md: 6, lg: 6 },
          name: 'effectiveOn',
          label: 'Transaction Effective Date',
          fieldType: FormFieldType.DatePickerField,
          disabled: !!policyTransaction,
          required: true,
          defaultValue: effectiveOnDate ? fakeLocalMidnightFromTZMidnight(new Date(effectiveOnDate)) : null,
          fieldConfig: {
            dateField: {
              minDate: fakeLocalMidnightFromTZMidnight(minEffectiveOnDate),
              maxDate: fakeLocalMidnightFromTZMidnight(maxExpiresOnDate),
            },
          },
        },
        reason: {
          gridWidth: { xs: 12 },
          name: 'reason',
          label: 'Endorsement Reason',
          fieldType: FormFieldType.SelectField,
          required: true,
          defaultValue: policyTransaction?.policyEndorsementTransaction?.reason ?? '',
          sx: { width: '100%', marginBottom: '24px' },
          fieldConfig: {
            selectField: {
              options: endorsementReasons,
            },
          },
        },
        description: {
          gridWidth: { xs: 12 },
          name: 'description',
          label: 'Description',
          fieldType: FormFieldType.TextField,
          required: true,
          defaultValue: policyTransaction?.description ?? '',
          sx: { width: '100%', marginBottom: '24px' },
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
            },
          },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.Endorsement,
        fields,
        title: `Endorse Policy ${formattedPolicyNumber}`,
      };
    }
    case EndorsementPolicyDialogType.UpdateBillPlan: {
      const effectiveOnDate = policyTransaction ? policyTransaction?.effectiveOn : (policy?.effectiveOn ?? null);
      const defaultBillingPlan = policyTransaction?.billingPlanId ?? policy.billingPlan?.id;
      const fields: { [fieldName: string]: TFormField } = {
        effectiveOn: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'effectiveOn',
          label: 'Transaction Effective Date',
          placeholder: 'Effective Date',
          fieldType: FormFieldType.DatePickerField,
          disabled: !!policyTransaction,
          required: true,
          defaultValue: effectiveOnDate ? fakeLocalMidnightFromTZMidnight(new Date(effectiveOnDate)) : null,
          sx: { marginBottom: '24px' },
          fieldConfig: {
            dateField: {
              minDate: fakeLocalMidnightFromTZMidnight(minEffectiveOnDate),
              maxDate: fakeLocalMidnightFromTZMidnight(maxExpiresOnDate),
            },
          },
        },
        policyBillPlan: {
          gridWidth: { xs: 12 },
          name: 'policyBillPlan',
          label: 'Policy Bill Plan',
          fieldType: FormFieldType.SelectField,
          required: true,
          defaultValue: defaultBillingPlan,
          sx: { width: '100%', marginBottom: '24px' },
          fieldConfig: {
            selectField: {
              options:
                other?.billingPlanOptions?.map((b) => ({ ...b, disabled: b.value === defaultBillingPlan })) ?? [],
            },
          },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.BillingPlanEndorsement,
        fields,
        title: `Endorse Policy ${formattedPolicyNumber}`,
        toastConfig: {
          successMessage: 'Policy bill plan endorsement succeeded',
        },
      };
    }

    case PolicyDialogType.Rewrite: {
      const effectiveOnDate = policyTransaction ? policyTransaction?.effectiveOn : (policy?.effectiveOn ?? null);

      const fields: { [fieldName: string]: TFormField } = {
        effectiveOn: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'effectiveOn',
          label: 'New Policy Effective Date',
          placeholder: 'Effective Date',
          fieldType: FormFieldType.DatePickerField,
          disabled: !!policyTransaction,
          required: true,
          defaultValue: effectiveOnDate ? fakeLocalMidnightFromTZMidnight(new Date(effectiveOnDate)) : null,
        },
        retainPolicyNumber: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'retainPolicyNumber',
          label: 'Retain Policy Number',
          disabled: !!policyTransaction,
          fieldType: FormFieldType.RadioGroupField,
          required: true,
          defaultValue: policyTransaction
            ? policyTransaction?.policyRewriteTransaction?.useSamePolicyNumber
              ? 'true'
              : 'false'
            : 'true',
          sx: (theme: Theme) => ({
            '.MuiFormControlLabel-label': getTypographyVariants(theme.palette.fontSize).badge,
          }),
          fieldConfig: {
            radioField: {
              options: BOOL_ITEMS,
            },
          },
        },
        description: {
          gridWidth: { xs: 12 },
          name: 'description',
          label: 'Description',
          disabled: !!policyTransaction,
          fieldType: FormFieldType.TextField,
          required: true,
          defaultValue: policyTransaction?.description ?? '',
          sx: { width: '100%', marginBottom: '20px' },
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
              maxLength: 500,
            },
          },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.NewBusinessRewrite,
        fields,
        title: `Rewrite Policy ${formattedPolicyNumber}`,
        buttonConfig: {
          createButtonText: 'Create Rewrite',
          continueButtonText: 'Continue Rewrite',
          viewButtonText: 'View Rewrite',
        },
      };
    }

    case PolicyDialogType.Cancelation: {
      const isDisabled =
        policyTransaction?.status === PolicyTransactionStatus.Processed ||
        policyTransaction?.status === PolicyTransactionStatus.RolledBack;
      const cancelationGroundsOptions = other?.cancelationGroundsOptions ?? [];

      // Expired policies can only be cancelled within their most recent term, at the insured's request,
      // and not flat.
      const isExpiredPolicy = policy.status === PolicyStatus.Expired && !isDisabled;
      const mostRecentTerm = maxBy(policyTerms, (term) => new Date(term.effectiveOn).getTime());
      const cancelationMinDate = isExpiredPolicy
        ? (mostRecentTerm?.effectiveOn ?? policy.effectiveOn)
        : minEffectiveOnDate;
      const fields: { [fieldName: string]: TFormField } = {
        reason: {
          gridWidth: { xs: 12 },
          name: 'reason',
          label: 'Reason for Cancellation',
          fieldType: FormFieldType.SelectField,
          disabled: isDisabled,
          required: true,
          defaultValue:
            policyTransaction?.policyCancelationTransaction?.reason ??
            (isExpiredPolicy ? PolicyCancelationReason.InsuredRequest : undefined),
          fieldConfig: {
            selectField: {
              options: isExpiredPolicy ? expiredPolicyCancelationReasons : cancelationReasons,
            },
          },
        },
        source: {
          gridWidth: { xs: 12 },
          watchedField: 'reason',
          isVisible: ({ data }: { data: { watchedField: string } }) => {
            return data.watchedField === 'Other';
          },
          name: 'source',
          label: 'Source',
          fieldType: FormFieldType.SelectField,
          disabled: isDisabled,
          required: true,
          defaultValue: policyTransaction?.policyCancelationTransaction?.source,
          fieldConfig: {
            selectField: {
              options: cancelationSources,
            },
          },
        },
        method: {
          gridWidth: { xs: 12 },
          name: 'method',
          label: 'Method',
          fieldType: FormFieldType.SelectField,
          disabled: isDisabled,
          required: true,
          defaultValue: policyTransaction?.policyCancelationTransaction?.method,
          fieldConfig: {
            selectField: {
              options: isExpiredPolicy ? expiredPolicyCancelationMethods : cancelationMethods,
            },
          },
        },
        effectiveDate: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'effectiveDate',
          label: 'Effective Date',
          fieldType: FormFieldType.DatePickerField,
          required: true,
          disabled: isDisabled,
          defaultValue: policyTransaction?.effectiveOn
            ? fakeLocalMidnightFromTZMidnight(new Date(policyTransaction?.effectiveOn))
            : null,

          fieldConfig: {
            dateField: {
              minDate: fakeLocalMidnightFromTZMidnight(new Date(cancelationMinDate)),
              maxDate: fakeLocalMidnightFromTZMidnight(maxExpiresOnDate),
            },
          },
        },
        grounds: {
          gridWidth: { xs: 12 },
          name: 'grounds',
          label: 'Grounds',
          fieldType: FormFieldType.SelectField,
          watchedField: 'reason',
          isVisible: ({ data }: { data: { watchedField: string } }) =>
            data.watchedField === PolicyCancelationReason.CompanyRequest,
          required: true,
          disabled: ({ data }: { data: { watchedField: string } }) =>
            isDisabled || data.watchedField !== PolicyCancelationReason.CompanyRequest,
          defaultValue: policyTransaction?.policyCancelationTransaction?.grounds,
          fieldConfig: {
            selectField: {
              options: cancelationGroundsOptions,
            },
          },
        },
        underwriterJustification: {
          gridWidth: { xs: 12 },
          name: 'underwriterJustification',
          label: 'Underwriter Justification',
          fieldType: FormFieldType.TextField,
          watchedField: 'reason',
          isVisible: ({ data }: { data: { watchedField: string } }) =>
            data.watchedField === PolicyCancelationReason.CompanyRequest,
          required: true,
          disabled: ({ data }: { data: { watchedField: string } }) =>
            isDisabled || data.watchedField !== PolicyCancelationReason.CompanyRequest,
          defaultValue: policyTransaction?.policyCancelationTransaction?.underwriterJustification ?? '',
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
            },
          },
        },
        certifiedMailRequired: {
          gridWidth: { xs: 12 },
          name: 'certifiedMailRequired',
          label: 'Certified Mail Required',
          fieldType: FormFieldType.SelectField,
          watchedField: 'reason',
          isVisible: ({ data }: { data: { watchedField: string } }) =>
            data.watchedField === PolicyCancelationReason.CompanyRequest,
          required: true,
          disabled: ({ data }: { data: { watchedField: string } }) =>
            isDisabled || data.watchedField !== PolicyCancelationReason.CompanyRequest,
          defaultValue:
            policyTransaction?.policyCancelationTransaction?.certifiedMailRequired != null
              ? policyTransaction.policyCancelationTransaction.certifiedMailRequired
                ? 'true'
                : 'false'
              : 'false',
          fieldConfig: {
            selectField: {
              options: BOOL_ITEMS,
            },
          },
        },
        blockCustomer: {
          gridWidth: { xs: 12 },
          name: 'blockCustomer',
          label: 'Block Customer?',
          fieldType: FormFieldType.SelectField,
          watchedField: 'reason',
          isVisible: ({ data }: { data: { watchedField: string } }) =>
            data.watchedField === PolicyCancelationReason.CompanyRequest,
          required: true,
          disabled: ({ data }: { data: { watchedField: string } }) =>
            isDisabled || data.watchedField !== PolicyCancelationReason.CompanyRequest,
          defaultValue: 'true',
          fieldConfig: {
            selectField: {
              options: BOOL_ITEMS,
            },
          },
        },
        notes: {
          gridWidth: { xs: 12 },
          name: 'notes',
          label: 'Notes',
          disabled: isDisabled,
          required: true,
          fieldType: FormFieldType.TextField,
          defaultValue: policyTransaction?.description ?? '',
          sx: { width: '100%' },
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
              maxLength: 500,
            },
          },
        },
        confirmation: {
          gridWidth: { xs: 12 },
          name: 'confirmation',
          disabled: isDisabled,
          label: `I'm sure that I want to cancel this policy`,
          required: true,
          fieldType: FormFieldType.CheckboxField,
          sx: { width: '100%', marginLeft: '-6px' },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.Cancelation,
        fields,
        title: `Cancellation of Policy ${formattedPolicyNumber}`,
        buttonConfig: {
          continueButtonText: 'Cancel this Policy',
          createButtonText: 'Cancel this Policy',
          hidePrimaryButton: !!policyTransaction,
        },
        toastConfig: {
          successMessage: 'Policy cancellation succeeded',
        },
      };
    }

    case PolicyDialogType.Reinstatement: {
      const fields: { [fieldName: string]: TFormField } = {
        reason: {
          gridWidth: { xs: 12 },
          name: 'reason',
          label: 'Reason for Reinstatement',
          fieldType: FormFieldType.SelectField,
          disabled: policyTransaction?.status === PolicyTransactionStatus.Processed,
          required: true,
          defaultValue: policyTransaction?.policyReinstatementTransaction?.reason,
          fieldConfig: {
            selectField: {
              options: reinstatementReasons,
            },
          },
        },
        effectiveDate: {
          gridWidth: { xs: 12, sm: 6 },
          name: 'effectiveDate',
          label: 'Effective On',
          fieldType: FormFieldType.DatePickerField,
          required: true,
          disabled: true,
          defaultValue: policyTransaction?.effectiveOn
            ? fakeLocalMidnightFromTZMidnight(new Date(policyTransaction.effectiveOn))
            : other?.lastCancelationEffectiveDate
              ? fakeLocalMidnightFromTZMidnight(new Date(other.lastCancelationEffectiveDate))
              : null,
        },
        description: {
          gridWidth: { xs: 12 },
          name: 'description',
          label: 'Description',
          disabled: policyTransaction?.status === PolicyTransactionStatus.Processed,
          required: true,
          fieldType: FormFieldType.TextField,
          defaultValue: policyTransaction?.description ?? '',
          sx: { width: '100%', marginBottom: '20px' },
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
              maxLength: 500,
            },
          },
        },
        certifiedMailRequired: {
          gridWidth: { xs: 12 },
          name: 'certifiedMailRequired',
          label: 'Certified Mail Required',
          fieldType: FormFieldType.SelectField,
          disabled: policyTransaction?.status === PolicyTransactionStatus.Processed,
          required: true,
          defaultValue: policyTransaction?.policyReinstatementTransaction?.certifiedMailRequired ? 'true' : 'false',
          fieldConfig: {
            selectField: {
              options: BOOL_ITEMS,
            },
          },
        },
        claimsLossesPostCancelation: {
          gridWidth: { xs: 12 },
          name: 'claimsLossesPostCancelation',
          label: 'Claims or Losses post cancellation?',
          fieldType: FormFieldType.SelectField,
          disabled: policyTransaction?.status === PolicyTransactionStatus.Processed,
          required: true,
          defaultValue: policyTransaction?.policyReinstatementTransaction?.claimsLossesPostCancelation
            ? 'true'
            : 'false',
          fieldConfig: {
            selectField: {
              options: BOOL_ITEMS,
            },
          },
        },
        confirmation: {
          gridWidth: { xs: 12 },
          name: 'confirmation',
          label: `I'm sure that I want to reinstate this policy`,
          required: true,
          fieldType: FormFieldType.CheckboxField,
          sx: { width: '100%', marginBottom: '20px' },
        },
      };

      return {
        transactionType: PolicyTransactionTypes.Reinstatement,
        fields,
        title: `Reinstatement of Policy ${formattedPolicyNumber}`,
        buttonConfig: {
          createButtonText: 'Reinstate Policy',
          hidePrimaryButton: !!policyTransaction,
        },
        toastConfig: {
          successMessage: 'Policy successfully reinstated',
        },
      };
    }

    case PolicyDialogType.Rescindment: {
      const customerIsBlocked = Boolean((policy as PolicyGetResponse)?.customer?.isBlocked);
      return {
        transactionType: 'Rollback',
        fields: {},
        title: customerIsBlocked
          ? `Customer is currently blocked - to rescind this cancellation you will need to unblock the customer. Do you want to rescind and unblock?`
          : `Are you sure you want to rescind the cancellation?`,
        buttonConfig: {
          createButtonText: customerIsBlocked ? 'Yes, unblock and rescind cancel' : 'Yes',
          cancelButtonText: customerIsBlocked ? 'No, exit' : 'No',
        },
        toastConfig: {
          errorMessage: 'Rescindment of Policy Cancellation failed',
          successMessage: 'Policy cancellation successfully rescinded',
        },
      };
    }

    case RenewDialogType.DoNotRenew: {
      const legalNoticeDaysRequirementMet = isLegalNoticeDaysRequirementMet(policy);

      if (!legalNoticeDaysRequirementMet) {
        return {
          transactionType: PolicyTransactionTypes.DoNotRenew,
          fields: {},
          title: `Mark ${formattedPolicyNumber} for Nonrenewal`,
          buttonConfig: {
            hidePrimaryButton: true,
            cancelButtonText: 'Exit',
          },
          toastConfig: {
            errorMessage: 'Creation of transaction failed',
            successMessage: 'Policy marked for non-renewal',
          },
        };
      }

      const nonrenewalGroundsOptions = getNonrenewalGrounds(policy).map((item) => ({
        label: item,
        value: item,
      }));

      return {
        transactionType: PolicyTransactionTypes.DoNotRenew,
        fields: {
          grounds: {
            gridWidth: { xs: 12 },
            name: 'grounds',
            label: 'Grounds',
            fieldType: FormFieldType.SelectField,
            required: true,
            defaultValue: policyTransaction?.policyNonrenewalTransaction?.grounds ?? '',
            fieldConfig: {
              selectField: {
                options: nonrenewalGroundsOptions,
              },
            },
          },
          underwriterJustification: {
            gridWidth: { xs: 12 },
            name: 'underwriterJustification',
            label: 'Underwriter Justification',
            fieldType: FormFieldType.TextField,
            required: true,
            defaultValue: policyTransaction?.policyNonrenewalTransaction?.underwriterJustification ?? '',
            fieldConfig: {
              textField: {
                rows: 4,
                multiline: true,
              },
            },
          },
          blockCustomer: {
            gridWidth: { xs: 12 },
            name: 'blockCustomer',
            label: 'Block Customer?',
            fieldType: FormFieldType.SelectField,
            required: true,
            defaultValue: 'true',
            fieldConfig: {
              selectField: {
                options: BOOL_ITEMS,
              },
            },
          },
        },
        title: `Mark ${formattedPolicyNumber} for Nonrenewal`,
        buttonConfig: {
          createButtonText: 'Mark for Nonrenewal',
          cancelButtonText: 'Exit',
        },
        toastConfig: {
          errorMessage: 'Creation of transaction failed',
          successMessage: 'Policy marked for non-renewal',
        },
      };
    }

    case RenewDialogType.DoNotRenewView: {
      const nonrenewalGroundsOptions = getNonrenewalGrounds(policy).map((item) => ({
        label: item,
        value: item,
      }));
      const existingGrounds = policyTransaction?.policyNonrenewalTransaction?.grounds;
      if (existingGrounds && !nonrenewalGroundsOptions.some((o) => o.value === existingGrounds)) {
        nonrenewalGroundsOptions.unshift({ label: existingGrounds, value: existingGrounds });
      }

      return {
        transactionType: PolicyTransactionTypes.DoNotRenew,
        fields: {
          grounds: {
            gridWidth: { xs: 12 },
            name: 'grounds',
            label: 'Grounds',
            fieldType: FormFieldType.SelectField,
            required: true,
            defaultValue: existingGrounds ?? '',
            fieldConfig: {
              selectField: {
                options: nonrenewalGroundsOptions,
              },
            },
          },
          underwriterJustification: {
            gridWidth: { xs: 12 },
            name: 'underwriterJustification',
            label: 'Underwriter Justification',
            fieldType: FormFieldType.TextField,
            required: true,
            defaultValue: policyTransaction?.policyNonrenewalTransaction?.underwriterJustification ?? '',
            fieldConfig: {
              textField: {
                rows: 4,
                multiline: true,
              },
            },
          },
        },
        title: `Mark ${formattedPolicyNumber} for Nonrenewal`,
        buttonConfig: {
          hidePrimaryButton: true,
          cancelButtonText: 'Exit',
        },
      };
    }

    case RenewDialogType.RescindDoNotRenew: {
      const isCustomerBlocked = Boolean((policy as any)?.customer?.isBlocked);
      return {
        transactionType: PolicyTransactionTypes.RescindDoNotRenew,
        fields: {},
        title: isCustomerBlocked
          ? `Rescinding nonrenewal will enable renewal transactions to be started for this policy. Also, the customer is currently blocked - to rescind this nonrenewal you will need to unblock the customer. Do you want to rescind and unblock?`
          : `Rescinding nonrenewal will enable renewal transactions to be started for this policy. Are you sure you want to rescind nonrenewal?`,
        buttonConfig: {
          cancelButtonText: isCustomerBlocked ? 'No, Exit' : 'No',
          createButtonText: isCustomerBlocked ? 'Yes, Unblock And Rescind Nonrenewal' : 'Yes',
        },
        toastConfig: {
          errorMessage: 'Creation of transaction failed',
          successMessage: 'Policy marked for renewal',
        },
      };
    }

    case RenewDialogType.ConditionalRenewal:
    case RenewDialogType.ConditionalRenewalView: {
      const legalNoticeDaysRequirementMet = isLegalNoticeDaysRequirementMet(policy);
      const selectedGrounds = other?.selectedConditionalGrounds ?? {};
      const conditionalRenewalGroundsOptions = getConditionalRenewalGrounds(policy);

      const getGroundsOptions = (fieldName: 'grounds1' | 'grounds2' | 'grounds3') => {
        const selectedSet = new Set(
          [selectedGrounds.grounds1, selectedGrounds.grounds2, selectedGrounds.grounds3].filter(Boolean),
        );
        const selectedValue = selectedGrounds[fieldName];

        if (selectedValue) {
          selectedSet.delete(selectedValue);
        }

        return conditionalRenewalGroundsOptions.map((item) => ({
          value: item,
          label: item,
          disabled: selectedSet.has(item),
        }));
      };

      const validateUniqueGroundsSelection = (_value: string, formValues?: Record<string, unknown>) => {
        const values = [formValues?.grounds1, formValues?.grounds2, formValues?.grounds3].filter(Boolean);
        const unique = new Set(values);
        return values.length === unique.size || 'Grounds selections must be unique';
      };

      const isReadOnlyDialog = dialogType === RenewDialogType.ConditionalRenewalView;

      if (!legalNoticeDaysRequirementMet && !isReadOnlyDialog) {
        return {
          transactionType: PolicyTransactionTypes.ConditionalRenewal,
          fields: {},
          title: `Create Conditional Renewal for ${formattedPolicyNumber}`,
          buttonConfig: {
            hidePrimaryButton: true,
            cancelButtonText: 'Exit',
          },
          toastConfig: {
            errorMessage: 'Creation of transaction failed',
            successMessage: 'Conditional renewal created',
          },
        };
      }

      return {
        transactionType: PolicyTransactionTypes.ConditionalRenewal,
        fields: {
          grounds1: {
            gridWidth: { xs: 12 },
            name: 'grounds1',
            label: 'Grounds 1',
            fieldType: FormFieldType.SelectField,
            required: true,
            defaultValue: (policyTransaction as any)?.policyConditionalRenewalTransaction?.grounds1 ?? '',
            fieldConfig: {
              selectField: {
                options: getGroundsOptions('grounds1'),
                validate: validateUniqueGroundsSelection,
              },
            },
          },
          grounds2: {
            gridWidth: { xs: 12 },
            name: 'grounds2',
            label: 'Grounds 2',
            fieldType: FormFieldType.SelectField,
            defaultValue: (policyTransaction as any)?.policyConditionalRenewalTransaction?.grounds2 ?? '',
            fieldConfig: {
              selectField: {
                options: getGroundsOptions('grounds2'),
                validate: validateUniqueGroundsSelection,
              },
            },
          },
          grounds3: {
            gridWidth: { xs: 12 },
            name: 'grounds3',
            label: 'Grounds 3',
            fieldType: FormFieldType.SelectField,
            defaultValue: (policyTransaction as any)?.policyConditionalRenewalTransaction?.grounds3 ?? '',
            fieldConfig: {
              selectField: {
                options: getGroundsOptions('grounds3'),
                validate: validateUniqueGroundsSelection,
              },
            },
          },
          underwriterJustification: {
            gridWidth: { xs: 12 },
            name: 'underwriterJustification',
            label: 'Underwriter Justification',
            fieldType: FormFieldType.TextField,
            required: true,
            defaultValue:
              (policyTransaction as any)?.policyConditionalRenewalTransaction?.underwriterJustification ?? '',
            fieldConfig: {
              textField: {
                rows: 4,
                multiline: true,
              },
            },
          },
          changeInCoverageDetails: {
            gridWidth: { xs: 12 },
            name: 'changeInCoverageDetails',
            label: 'Change in Coverage Details',
            fieldType: FormFieldType.TextField,
            required: true,
            defaultValue:
              (policyTransaction as any)?.policyConditionalRenewalTransaction?.changeInCoverageDetails ?? '',
            fieldConfig: {
              textField: {
                rows: 4,
                multiline: true,
              },
            },
          },
        },
        title: isReadOnlyDialog
          ? `Conditional Renewal for ${formattedPolicyNumber}`
          : `Create Conditional Renewal for ${formattedPolicyNumber}`,
        buttonConfig: {
          createButtonText: isReadOnlyDialog ? undefined : 'Create Conditional Renewal',
          cancelButtonText: 'Exit',
          hidePrimaryButton: isReadOnlyDialog,
        },
        toastConfig: {
          errorMessage: 'Creation of transaction failed',
          successMessage: 'Conditional renewal created',
        },
      };
    }

    case PolicyDialogType.ManageRoles: {
      const formFields: { [fieldName: string]: TFormField } = {
        brokerage: {
          gridWidth: { xs: 12 },
          name: 'brokerage',
          label: 'Brokerage',
          fieldType: FormFieldType.SelectField,
          required: true,
          disabled: true,
          defaultValue: policy?.brokerage?.id ?? '',
          fieldConfig: {
            selectField: {
              options: policy?.brokerage?.id
                ? [
                    {
                      value: policy?.brokerage?.id,
                      label: policy?.brokerage?.name,
                    },
                  ]
                : [],
            },
          },
        },
        producer: {
          gridWidth: { xs: 12 },
          name: 'producer',
          label: 'Assigned Producer',
          fieldType: FormFieldType.FilteredSelectField,
          required: true,
          disabled: false,
          defaultValue: policy?.brokerageProducer?.id
            ? {
                value: policy?.brokerageProducer?.id,
                label: `${policy?.brokerageProducer?.user.firstName + ' ' + policy.brokerageProducer.user.lastName}`,
              }
            : '',

          fieldConfig: {
            selectField: {
              handleInputChange: other?.producerField?.onDebouncedChange,
              handleOnScroll: (e) => {
                if (other?.producerField?.loadMore) {
                  handleScrollToBottom(e, other.producerField.loadMore);
                }
              },
              options: other?.producerField?.brokerageProducers?.pages
                ?.flatMap((page) => page.items)
                .map((bp: BrokerageProducerSearchResponse['items'][0]) => ({
                  value: bp.id,
                  label: bp.user.fullName,
                })),
            },
          },
        },
        description: {
          gridWidth: { xs: 12 },
          name: 'description',
          label: 'Reason',
          fieldType: FormFieldType.TextField,
          required: false,
          defaultValue: policyTransaction?.description ?? '',
          sx: { width: '100%', marginBottom: '20px' },
          fieldConfig: {
            textField: {
              rows: 4,
              multiline: true,
              maxLength: 500,
            },
          },
        },
      };
      return {
        transactionType: PolicyTransactionTypes.ManageRoles,
        fields: formFields,
        title: `Assigned Roles for Policy ${formattedPolicyNumber}`,
        toastConfig: {
          successMessage: 'Roles updated',
          errorMessage: 'Failed to update roles',
        },
        buttonConfig: {
          createButtonText: 'Update Assignments',
        },
      };
    }

    default:
      throw Error('Policy Transaction Type not implemented');
  }
};
